import React, { useState, useEffect } from 'react'
import { User, Settings as SettingsIcon, Bell, Palette, Check, Monitor, Sun, Moon, Shield, Download, Trash2, Headphones, Mail, ChevronRight } from 'lucide-react'
import { FILL_GRADIENT, LINE_GRADIENT } from '../constants/gradients'
import authService from '../services/authService'
import { imageUploadService } from '../services/imageUploadService'
import privacyService from '../services/privacyService'
import { useThemeStore } from '../store/useThemeStore'
import toast from 'react-hot-toast'
import { SUPPORT_CONTACTS } from '../config/support'

const WhatsAppIcon = ({ size = 22, color = '#25D366' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.488"
      fill={color}
    />
  </svg>
)

const TelegramIcon = ({ size = 22, color = '#0088CC' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"
      fill={color}
    />
  </svg>
)

const ProfilePage = () => {
  const [activeTab, setActiveTab] = useState<'profile' | 'account' | 'appearance' | 'notifications' | 'privacy' | 'support'>('profile')
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const { mode, setThemeMode } = useThemeStore()
  
  // Profile form state
  const [displayName, setDisplayName] = useState('')
  const [username, setUsername] = useState('')
  const [bio, setBio] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [avatarUrl, setAvatarUrl] = useState('')
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  
  // Original values for change detection
  const [originalValues, setOriginalValues] = useState({
    displayName: '',
    username: '',
    bio: '',
    websiteUrl: '',
    avatarUrl: ''
  })

  useEffect(() => {
    loadCurrentUser()
  }, [])

  const loadCurrentUser = async () => {
    try {
      setLoading(true)
      const userData = await authService.getCurrentUser()
      const user = userData.user || userData
      setCurrentUser(user)
      
      // Initialize form fields
      const initialDisplayName = user.display_name || user.displayName || ''
      const initialUsername = user.username || ''
      const initialBio = user.bio || ''
      const initialWebsiteUrl = user.website_url || user.websiteUrl || ''
      const initialAvatarUrl = user.image_url || user.imageUrl || ''
      
      setDisplayName(initialDisplayName)
      setUsername(initialUsername)
      setBio(initialBio)
      setWebsiteUrl(initialWebsiteUrl)
      setAvatarUrl(initialAvatarUrl)
      
      // Store original values
      setOriginalValues({
        displayName: initialDisplayName,
        username: initialUsername,
        bio: initialBio,
        websiteUrl: initialWebsiteUrl,
        avatarUrl: initialAvatarUrl
      })
    } catch (error) {
      console.error('Failed to load user:', error)
      toast.error('Failed to load user data')
    } finally {
      setLoading(false)
    }
  }

  const handleAvatarUpload = async (file: File) => {
    if (!currentUser) return
    
    let preview: string | null = null
    
    try {
      setIsUploadingAvatar(true)
      
      // Generate and show preview while uploading
      preview = imageUploadService.generatePreviewUrl(file)
      setAvatarUrl(preview)
      
      // Upload with moderation
      const result = await imageUploadService.uploadImage(
        file,
        currentUser.id,
        'avatar'
      )
      
      // Clean up preview and show final URL (but don't save to profile yet)
      imageUploadService.cleanupPreviewUrl(preview)
      setAvatarUrl(result.url)
      
      toast.success('Avatar uploaded. Click "Save Profile" to update.')
    } catch (error) {
      console.error('Failed to upload avatar:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload avatar'
      
      // Remove preview if moderation failed
      if (preview) {
        imageUploadService.cleanupPreviewUrl(preview)
        setAvatarUrl(currentUser.image_url || currentUser.imageUrl || '')
      }
      
      toast.error(errorMessage)
    } finally {
      setIsUploadingAvatar(false)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleAvatarUpload(file)
    }
  }

  const hasChanges = () => {
    return (
      displayName !== originalValues.displayName ||
      username !== originalValues.username ||
      bio !== originalValues.bio ||
      websiteUrl !== originalValues.websiteUrl ||
      avatarUrl !== originalValues.avatarUrl
    )
  }

  const handleSaveProfile = async () => {
    if (!currentUser) return

    try {
      setIsSaving(true)
      
      // Call actual API endpoint to update user profile
      await authService.updateProfile({
        display_name: displayName,
        image_url: avatarUrl
      })

      toast.success('Profile updated successfully')
      await loadCurrentUser()
    } catch (error: any) {
      console.error('Failed to update profile:', error)
      toast.error(error.response?.data?.message || 'Failed to update profile')
    } finally {
      setIsSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-transparent border-t-pink-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-dark-text-muted font-medium">Loading profile...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 dark:bg-dark-bg">
      <div className="max-w-7xl mx-auto p-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl overflow-hidden sticky top-8 min-h-[calc(100vh-9rem)]">
              <button
                onClick={() => setActiveTab('profile')}
                className={`w-full flex items-center gap-3 pl-3 pr-4 py-3 text-left transition-all duration-200 relative ${
                  activeTab === 'profile'
                    ? 'bg-gray-50 dark:bg-gray-800/50 font-semibold'
                    : 'text-gray-600 dark:text-dark-text-muted hover:bg-gray-50 dark:hover:bg-gray-800/50 font-medium'
                }`}
              >
                {activeTab === 'profile' && (
                  <div 
                    className="absolute left-0 top-0 bottom-0 w-0.5"
                    style={{ background: LINE_GRADIENT }}
                  />
                )}
                <User className={`w-5 h-5 ${activeTab === 'profile' ? 'text-pink-600' : ''}`} />
                <span 
                  className={activeTab === 'profile' ? '' : 'text-gray-900 dark:text-dark-text'}
                  style={activeTab === 'profile' ? {
                    background: LINE_GRADIENT,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  } : {}}
                >
                  Public profile
                </span>
              </button>
              <button
                onClick={() => setActiveTab('account')}
                className={`w-full flex items-center gap-3 pl-3 pr-4 py-3 text-left transition-all duration-200 relative ${
                  activeTab === 'account'
                    ? 'bg-gray-50 dark:bg-gray-800/50 font-semibold'
                    : 'text-gray-600 dark:text-dark-text-muted hover:bg-gray-50 dark:hover:bg-gray-800/50 font-medium'
                }`}
              >
                {activeTab === 'account' && (
                  <div 
                    className="absolute left-0 top-0 bottom-0 w-0.5"
                    style={{ background: LINE_GRADIENT }}
                  />
                )}
                <SettingsIcon className={`w-5 h-5 ${activeTab === 'account' ? 'text-pink-600' : ''}`} />
                <span 
                  className={activeTab === 'account' ? '' : 'text-gray-900 dark:text-dark-text'}
                  style={activeTab === 'account' ? {
                    background: LINE_GRADIENT,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  } : {}}
                >
                  Account
                </span>
              </button>
              <button
                onClick={() => setActiveTab('appearance')}
                className={`w-full flex items-center gap-3 pl-3 pr-4 py-3 text-left transition-all duration-200 relative ${
                  activeTab === 'appearance'
                    ? 'bg-gray-50 dark:bg-gray-800/50 font-semibold'
                    : 'text-gray-600 dark:text-dark-text-muted hover:bg-gray-50 dark:hover:bg-gray-800/50 font-medium'
                }`}
              >
                {activeTab === 'appearance' && (
                  <div 
                    className="absolute left-0 top-0 bottom-0 w-0.5"
                    style={{ background: LINE_GRADIENT }}
                  />
                )}
                <Palette className={`w-5 h-5 ${activeTab === 'appearance' ? 'text-pink-600' : ''}`} />
                <span 
                  className={activeTab === 'appearance' ? '' : 'text-gray-900 dark:text-dark-text'}
                  style={activeTab === 'appearance' ? {
                    background: LINE_GRADIENT,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  } : {}}
                >
                  Appearance
                </span>
              </button>
              <button
                onClick={() => setActiveTab('notifications')}
                className={`w-full flex items-center gap-3 pl-3 pr-4 py-3 text-left transition-all duration-200 relative ${
                  activeTab === 'notifications'
                    ? 'bg-gray-50 dark:bg-gray-800/50 font-semibold'
                    : 'text-gray-600 dark:text-dark-text-muted hover:bg-gray-50 dark:hover:bg-gray-800/50 font-medium'
                }`}
              >
                {activeTab === 'notifications' && (
                  <div 
                    className="absolute left-0 top-0 bottom-0 w-0.5"
                    style={{ background: LINE_GRADIENT }}
                  />
                )}
                <Bell className={`w-5 h-5 ${activeTab === 'notifications' ? 'text-pink-600' : ''}`} />
                <span 
                  className={activeTab === 'notifications' ? '' : 'text-gray-900 dark:text-dark-text'}
                  style={activeTab === 'notifications' ? {
                    background: LINE_GRADIENT,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  } : {}}
                >
                  Notifications
                </span>
              </button>
              <button
                onClick={() => setActiveTab('privacy')}
                className={`w-full flex items-center gap-3 pl-3 pr-4 py-3 text-left transition-all duration-200 relative ${
                  activeTab === 'privacy'
                    ? 'bg-gray-50 dark:bg-gray-800/50 font-semibold'
                    : 'text-gray-600 dark:text-dark-text-muted hover:bg-gray-50 dark:hover:bg-gray-800/50 font-medium'
                }`}
              >
                {activeTab === 'privacy' && (
                  <div 
                    className="absolute left-0 top-0 bottom-0 w-0.5"
                    style={{ background: LINE_GRADIENT }}
                  />
                )}
                <Shield className={`w-5 h-5 ${activeTab === 'privacy' ? 'text-pink-600' : ''}`} />
                <span 
                  className={activeTab === 'privacy' ? '' : 'text-gray-900 dark:text-dark-text'}
                  style={activeTab === 'privacy' ? {
                    background: LINE_GRADIENT,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  } : {}}
                >
                  Privacy & Data
                </span>
              </button>
              <button
                onClick={() => setActiveTab('support')}
                className={`w-full flex items-center gap-3 pl-3 pr-4 py-3 text-left transition-all duration-200 relative ${
                  activeTab === 'support'
                    ? 'bg-gray-50 dark:bg-gray-800/50 font-semibold'
                    : 'text-gray-600 dark:text-dark-text-muted hover:bg-gray-50 dark:hover:bg-gray-800/50 font-medium'
                }`}
              >
                {activeTab === 'support' && (
                  <div 
                    className="absolute left-0 top-0 bottom-0 w-0.5"
                    style={{ background: LINE_GRADIENT }}
                  />
                )}
                <Headphones className={`w-5 h-5 ${activeTab === 'support' ? 'text-pink-600' : ''}`} />
                <span 
                  className={activeTab === 'support' ? '' : 'text-gray-900 dark:text-dark-text'}
                  style={activeTab === 'support' ? {
                    background: LINE_GRADIENT,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  } : {}}
                >
                  Support
                </span>
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {activeTab === 'profile' && (
              <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl">
                <div className="p-8 border-b border-gray-200 dark:border-dark-border flex items-center justify-between">
                  <div>
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-dark-text">Public profile</h1>
                    <p className="text-gray-500 dark:text-dark-text-muted mt-1">
                      Your personal account
                    </p>
                  </div>
                  
                  <button
                    onClick={handleSaveProfile}
                    disabled={isSaving || isUploadingAvatar || !hasChanges()}
                    className={`px-6 py-2.5 text-sm font-semibold text-white rounded-lg transition-all duration-200 flex items-center gap-2 ${
                      isSaving || isUploadingAvatar || !hasChanges()
                        ? 'opacity-50'
                        : 'hover:shadow-lg hover:scale-105 active:scale-95'
                    }`}
                    style={!isSaving && !isUploadingAvatar && hasChanges() ? { background: FILL_GRADIENT } : { background: '#9CA3AF' }}
                  >
                    {isSaving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Saving...
                      </>
                    ) : (
                      'Update profile'
                    )}
                  </button>
                </div>

                <div className="p-8 space-y-6">
                  {/* Name and Username + Avatar */}
                  <div className="flex gap-8">
                    <div className="flex-1 space-y-6">
                      {/* Name */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
                          Name
                        </label>
                        <input
                          type="text"
                          value={displayName}
                          onChange={(e) => setDisplayName(e.target.value)}
                          className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-dark-border rounded-xl transition-colors focus:outline-none focus:border-gray-300 dark:focus:border-gray-600 dark:bg-dark-bg dark:text-dark-text font-medium"
                          placeholder="Your name"
                        />
                        <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-2">
                          Your name may appear around the platform where you contribute or are mentioned. You can remove it at any time.
                        </p>
                      </div>

                      {/* Username */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
                          Username
                        </label>
                        <input
                          type="text"
                          value={username}
                          onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, ''))}
                          className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-dark-border rounded-xl transition-colors focus:outline-none focus:border-gray-300 dark:focus:border-gray-600 dark:bg-dark-bg dark:text-dark-text font-medium"
                          placeholder="username"
                        />
                        <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-2">
                          Your unique username across the platform
                        </p>
                      </div>
                    </div>
                    
                    {/* Avatar Section */}
                    <div className="w-48 flex-shrink-0">
                      <div 
                        className="w-48 h-48 rounded-full flex items-center justify-center overflow-hidden border-4 border-gray-200 dark:border-gray-700 mb-4"
                        style={{ background: avatarUrl ? 'transparent' : FILL_GRADIENT }}
                      >
                        {avatarUrl ? (
                          <img 
                            src={avatarUrl} 
                            alt="Profile avatar" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-6xl font-bold text-white">
                            {(displayName || username || 'U').charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      
                      <label className="block w-full text-center px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 cursor-pointer border-2 border-gray-200 dark:border-gray-700">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileSelect}
                          disabled={isUploadingAvatar || isSaving}
                          className="hidden"
                        />
                        {isUploadingAvatar ? 'Uploading...' : 'Edit'}
                      </label>
                    </div>
                  </div>

                  {/* Bio */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
                      Bio
                    </label>
                    <textarea
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-dark-border rounded-xl transition-colors focus:outline-none focus:border-gray-300 dark:focus:border-gray-600 dark:bg-dark-bg dark:text-dark-text font-medium resize-none"
                      placeholder="Tell us a little bit about yourself"
                    />
                    <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-2">
                      You can <span className="text-pink-600 font-medium">@mention</span> other users and organizations to link to them.
                    </p>
                  </div>

                  {/* URL */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
                      URL
                    </label>
                    <input
                      type="url"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-dark-border rounded-xl transition-colors focus:outline-none focus:border-gray-300 dark:focus:border-gray-600 dark:bg-dark-bg dark:text-dark-text font-medium"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'account' && (
              <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-dark-text mb-2">Account</h2>
                <p className="text-gray-500 dark:text-dark-text-muted mb-8">
                  Manage your account settings and preferences
                </p>
                <div className="text-center py-12">
                  <SettingsIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-dark-text-muted">Account settings coming soon...</p>
                </div>
              </div>
            )}

            {activeTab === 'appearance' && (
              <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl">
                <div className="p-8 border-b border-gray-200 dark:border-dark-border">
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-dark-text">Appearance</h2>
                  <p className="text-gray-500 dark:text-dark-text-muted mt-1">
                    Customize how Beemi Studio looks for you
                  </p>
                </div>

                <div className="p-8">
                  <div className="space-y-6">
                    {/* Theme Mode */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-4">
                        Theme mode
                      </h3>
                      <div className="space-y-3">
                        {/* System */}
                        <button
                          onClick={() => setThemeMode('system')}
                          className={`w-full flex items-center justify-between p-4 border-2 rounded-xl transition-all duration-200 ${
                            mode === 'system'
                              ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/10'
                              : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-600'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Monitor className="w-8 h-8 text-gray-600 dark:text-gray-400" />
                            <div className="text-left">
                              <div className="font-semibold text-gray-900 dark:text-dark-text">System</div>
                              <div className="text-sm text-gray-500 dark:text-dark-text-muted">
                                Automatically switch between light and dark themes
                              </div>
                            </div>
                          </div>
                          {mode === 'system' && (
                            <Check className="w-6 h-6 text-pink-600 flex-shrink-0" />
                          )}
                        </button>

                        {/* Light */}
                        <button
                          onClick={() => setThemeMode('light')}
                          className={`w-full flex items-center justify-between p-4 border-2 rounded-xl transition-all duration-200 ${
                            mode === 'light'
                              ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/10'
                              : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-600'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Sun className="w-8 h-8 text-gray-600 dark:text-gray-400" />
                            <div className="text-left">
                              <div className="font-semibold text-gray-900 dark:text-dark-text">Light</div>
                              <div className="text-sm text-gray-500 dark:text-dark-text-muted">
                                Always use light theme
                              </div>
                            </div>
                          </div>
                          {mode === 'light' && (
                            <Check className="w-6 h-6 text-pink-600 flex-shrink-0" />
                          )}
                        </button>

                        {/* Dark */}
                        <button
                          onClick={() => setThemeMode('dark')}
                          className={`w-full flex items-center justify-between p-4 border-2 rounded-xl transition-all duration-200 ${
                            mode === 'dark'
                              ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/10'
                              : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-600'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Moon className="w-8 h-8 text-gray-600 dark:text-gray-400" />
                            <div className="text-left">
                              <div className="font-semibold text-gray-900 dark:text-dark-text">Dark</div>
                              <div className="text-sm text-gray-500 dark:text-dark-text-muted">
                                Always use dark theme
                              </div>
                            </div>
                          </div>
                          {mode === 'dark' && (
                            <Check className="w-6 h-6 text-pink-600 flex-shrink-0" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-dark-text mb-2">Notifications</h2>
                <p className="text-gray-500 dark:text-dark-text-muted mb-8">
                  Manage how you receive notifications
                </p>
                <div className="text-center py-12">
                  <Bell className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-dark-text-muted">Notification settings coming soon...</p>
                </div>
              </div>
            )}

            {activeTab === 'privacy' && (
              <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-dark-text mb-2">Privacy & Data</h2>
                <p className="text-gray-500 dark:text-dark-text-muted mb-8">
                  Manage your data and privacy settings (GDPR compliant)
                </p>

                <div className="space-y-6">
                  {/* Data Export Section */}
                  <div className="border border-gray-200 dark:border-dark-border rounded-lg p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                        <Download className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-2">
                          Download Your Data
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-4">
                          Export all your data including projects, account information, and activity history in JSON format. 
                          This complies with GDPR Article 20 (Right to Data Portability).
                        </p>
                        <button
                          onClick={async () => {
                            const exportPromise = privacyService.exportUserData();
                            toast.promise(exportPromise, {
                              loading: 'Preparing your data export...',
                              success: 'Data exported successfully! Check your downloads.',
                              error: (err) => `Export failed: ${err.error || 'Unknown error'}`
                            });
                          }}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                        >
                          <Download className="w-4 h-4" />
                          Export My Data
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Marketing Consent Section */}
                  <div className="border border-gray-200 dark:border-dark-border rounded-lg p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                        <Bell className="w-6 h-6 text-green-600 dark:text-green-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-2">
                          Marketing Communications
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-4">
                          Control whether you receive newsletters, feature announcements, and promotional emails.
                        </p>
                        <button
                          onClick={async () => {
                            const currentStatus = await privacyService.getConsentStatus();
                            const newConsent = !currentStatus.marketing;
                            const success = await privacyService.updateMarketingConsent(newConsent);
                            if (success) {
                              toast.success(newConsent ? 'Marketing emails enabled' : 'Marketing emails disabled');
                            } else {
                              toast.error('Failed to update preference');
                            }
                          }}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
                        >
                          Manage Consent
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Account Deletion Section */}
                  <div className="border border-red-200 dark:border-red-900/50 rounded-lg p-6 bg-red-50/30 dark:bg-red-900/5">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-lg">
                        <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-2">
                          Delete Account
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-4">
                          Permanently delete your account and all associated data. This action cannot be undone. 
                          All your projects will be removed from the platform. (GDPR Article 17: Right to Erasure)
                        </p>
                        <button
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to delete your account? This action is permanent and cannot be undone. All your projects will be removed.')) {
                              const result = await privacyService.deleteAccount('user_request', '');
                              if (result.success) {
                                toast.success('Account deletion scheduled. You will receive confirmation via email.');
                                setTimeout(() => {
                                  authService.logout();
                                  window.location.href = '/login';
                                }, 3000);
                              } else {
                                toast.error(result.error || 'Failed to delete account');
                              }
                            }
                          }}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete My Account
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Privacy Information */}
                  <div className="border border-gray-200 dark:border-dark-border rounded-lg p-6 bg-gray-50 dark:bg-gray-800/30">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-4">
                      Your Privacy Rights
                    </h3>
                    <ul className="space-y-2 text-gray-600 dark:text-gray-300 text-sm">
                      <li>✓ Right to access your data</li>
                      <li>✓ Right to correct inaccurate data</li>
                      <li>✓ Right to delete your data</li>
                      <li>✓ Right to data portability</li>
                      <li>✓ Right to withdraw consent</li>
                    </ul>
                    <p className="mt-4 text-sm text-gray-500 dark:text-dark-text-muted">
                      For questions or to exercise your rights, contact: <a href="mailto:dpo@qenetech.com" className="text-blue-600 hover:underline">dpo@qenetech.com</a>
                    </p>
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'support' && (
              <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl">
                <div className="p-8 border-b border-gray-200 dark:border-dark-border">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: FILL_GRADIENT }}
                    >
                      <Headphones className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-semibold text-gray-900 dark:text-dark-text">Support</h2>
                      <p className="text-gray-500 dark:text-dark-text-muted mt-0.5 text-sm">
                        Get in touch with the Beemi team
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-8 space-y-6">
                  {/* Direct contact */}
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-dark-text mb-3">
                      Contact us directly
                    </h3>
                    <div className="border border-gray-200 dark:border-dark-border rounded-xl overflow-hidden divide-y divide-gray-100 dark:divide-dark-border">
                      {[
                        {
                          key: 'whatsapp',
                          label: 'WhatsApp',
                          subtitle: 'Chat with us directly',
                          color: '#25D366',
                          url: SUPPORT_CONTACTS.whatsapp.url,
                          Icon: WhatsAppIcon,
                        },
                        {
                          key: 'telegram',
                          label: 'Telegram',
                          subtitle: 'Message our support bot',
                          color: '#0088CC',
                          url: SUPPORT_CONTACTS.telegram.url,
                          Icon: TelegramIcon,
                        },
                        {
                          key: 'email',
                          label: 'Email',
                          subtitle: SUPPORT_CONTACTS.email.address,
                          color: '#EA4335',
                          url: SUPPORT_CONTACTS.email.url,
                          Icon: ({ size = 22, color = '#EA4335' }: { size?: number; color?: string }) => (
                            <Mail size={size} color={color} />
                          ),
                        },
                      ].map(({ key, label, subtitle, color, url, Icon }) => (
                        <button
                          key={key}
                          onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                          className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left group"
                        >
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${color}18` }}
                          >
                            <Icon size={20} color={color} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 dark:text-dark-text text-sm">{label}</p>
                            <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-0.5 truncate">{subtitle}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  </div>

                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProfilePage

